<script setup lang="ts">
import { computed, ref, nextTick, onMounted, onUnmounted } from 'vue';
import { useTranslation } from 'i18next-vue';
import i18next from 'i18next';
import { DateTime } from 'luxon';
import { useFeedStore } from '@/client/stores/feedStore';
import { useToast } from '@/client/composables/useToast';
import { useFeedEvents } from '@/client/composables/useFeedEvents';
import { useFeedRepost, type PendingRepost } from '@/client/composables/useFeedRepost';
import DocLink from '@/client/components/common/doc-link.vue';
import EmptyState from '@/common/ui/components/EmptyState.vue';
import RepostCategoriesModal from '@/client/components/logged_in/repost-categories-modal.vue';
import ReportEventModal from '@/client/components/report-event.vue';
import FeedEventDetailModal from '@/client/components/logged_in/feed/feed-event-detail-modal.vue';
import { type FeedEvent, type CategoryEntry } from '@/client/service/feed';

const { t } = useTranslation('feed', { keyPrefix: 'events' });
const feedStore = useFeedStore();
const toast = useToast();
const { isLoading, loadMore: loadMoreFeed } = useFeedEvents();
const { repostEvent, unrepostEvent, confirmRepost } = useFeedRepost();

const events = computed(() => feedStore.events);
const hasMore = computed(() => feedStore.eventsHasMore);
const pendingRepost = ref<PendingRepost | null>(null);
const hasFollows = computed(() => feedStore.follows.length > 0);
const sentinelRef = ref(null);
const repostTriggerElement = ref(null);
const reportingEventId = ref<string | null>(null);
const reportingEventTitle = ref<string | null>(null);
const reportTriggerElement = ref<HTMLElement | null>(null);
const detailEvent = ref<FeedEvent | null>(null);
const detailTriggerElement = ref<HTMLElement | null>(null);
let observer = null;

/**
 * Format event date and time for display.
 * Reads the start date from the base schedule, falling back to the legacy date field.
 */
const formatEventDate = (event: FeedEvent) => {
  // Prefer the base schedule's start (the canonical source of truth)
  const scheduleStart = event.baseSchedule()?.startDate;
  if (scheduleStart) {
    try {
      // startDate is a Luxon DateTime in the schedule's zone; show it in the
      // viewer's zone, like the calendar view's instances.
      const dt: DateTime = scheduleStart instanceof DateTime
        ? scheduleStart
        : DateTime.fromISO(String(scheduleStart));
      return dt.toLocal().toLocaleString(DateTime.DATETIME_MED);
    }
    catch (error) {
      console.error('Error formatting schedule date:', error);
    }
  }

  // Fall back to the legacy date field for backward compatibility
  if (!event.date) {
    return '';
  }

  try {
    const dt = DateTime.fromISO(event.date);
    return dt.toLocaleString(DateTime.DATETIME_MED);
  }
  catch (error) {
    console.error('Error formatting date:', error);
    return event.date;
  }
};

/**
 * Get the event title in the appropriate language
 */
const getEventTitle = (event: FeedEvent) => {
  if (!event || typeof event.content !== 'function') {
    return '';
  }
  const content = event.content(i18next.language);
  return content?.name || t('untitled_event');
};

/**
 * Get the event description in the appropriate language
 */
const getEventDescription = (event: FeedEvent) => {
  const content = event.content(i18next.language);
  return content?.description || '';
};

/**
 * Get calendar identifier for display.
 * Uses the human-readable source calendar actor ID (e.g., calendar@domain.com)
 * included in each feed event by the backend.
 */
const getCalendarIdentifier = (event: FeedEvent) => {
  return event.sourceCalendarActorId || t('unknown_calendar');
};

/**
 * Get the event location name when available.
 * Returns the venue name for events that have location data, empty string otherwise.
 */
const getEventLocation = (event: FeedEvent) => {
  return event.location?.name || '';
};

/**
 * Derive the pre-selected category objects for the modal from pendingRepost state.
 * Matches preSelectedIds against allLocalCategories to get proper { id, name } pairs.
 */
const pendingRepostPreSelected = computed(() => {
  if (!pendingRepost.value) {
    return [];
  }
  const { preSelectedIds, allLocalCategories } = pendingRepost.value;
  return allLocalCategories.filter((cat) => preSelectedIds.includes(cat.id));
});

/**
 * Derive the full event object for the pending repost modal.
 * Passes the complete event so the modal can display event details.
 */
const pendingRepostEvent = computed(() => {
  if (!pendingRepost.value) {
    return null;
  }
  return events.value.find((e) => e.id === pendingRepost.value.eventId) ?? null;
});

/**
 * Handle repost button click — delegates to the composable which may return a PendingRepost
 */
const handleRepost = async (eventId: string, event: MouseEvent) => {
  repostTriggerElement.value = (event?.currentTarget) ?? null;
  try {
    const result = await repostEvent(eventId);
    if (result) {
      pendingRepost.value = result;
    }
  }
  catch (error) {
    console.error('Error reposting event:', error);
    toast.error(t('repost_error'));
  }
};

/**
 * Handle unrepost action (clicking on reposted label)
 */
const handleUnrepost = async (eventId: string) => {
  try {
    await unrepostEvent(eventId);
  }
  catch (error) {
    console.error('Error unreposting event:', error);
    toast.error(t('unrepost_error'));
  }
};

/**
 * Handle modal confirm: repost with the selected category IDs.
 * When sourceCategoriesToAdopt is provided (no-local-categories mode), the composable
 * will create those categories, save mappings, and repost with the new IDs.
 */
const handleRepostConfirm = async (categoryIds: string[], sourceCategoriesToAdopt?: CategoryEntry[]) => {
  if (!pendingRepost.value) {
    return;
  }

  const { eventId, calendarActorUuid } = pendingRepost.value;
  pendingRepost.value = null;

  try {
    await confirmRepost(eventId, categoryIds, calendarActorUuid, sourceCategoriesToAdopt);
    nextTick(() => { repostTriggerElement.value?.focus(); });
  }
  catch (error) {
    console.error('Error confirming repost:', error);
    toast.error(t('repost_error'));
  }
};

/**
 * Handle modal cancel: dismiss without reposting
 */
const handleRepostCancel = () => {
  pendingRepost.value = null;
  nextTick(() => { repostTriggerElement.value?.focus(); });
};

/**
 * Handle report button click — captures the trigger element and event title for focus restoration
 */
const handleReport = (event: FeedEvent, domEvent: MouseEvent) => {
  reportTriggerElement.value = (domEvent?.currentTarget as HTMLElement) ?? null;
  reportingEventId.value = event.id;
  reportingEventTitle.value = getEventTitle(event);
};

/**
 * Handle report modal close — clears event ID and title, restores focus to the trigger button
 */
const handleReportClose = () => {
  reportingEventId.value = null;
  reportingEventTitle.value = null;
  nextTick(() => { reportTriggerElement.value?.focus(); });
};

/**
 * Handle "Details" button click — opens the detail modal for the event
 */
const handleOpenDetail = (event: FeedEvent, domEvent: MouseEvent) => {
  detailTriggerElement.value = domEvent.currentTarget as HTMLElement;
  detailEvent.value = event;
};

/**
 * Handle detail modal close — clears the detail event and restores focus to the trigger button
 */
const handleCloseDetail = () => {
  detailEvent.value = null;
  nextTick(() => { detailTriggerElement.value?.focus(); });
};

/**
 * Handle report action emitted from the detail modal.
 * Closes the detail modal first, then opens the report modal.
 */
const handleReportFromModal = (domEvent: MouseEvent) => {
  if (detailEvent.value) {
    const eventToReport = detailEvent.value;
    // Set up report trigger as the detail modal's report button so focus
    // returns sensibly after the report modal closes
    reportTriggerElement.value = domEvent?.currentTarget as HTMLElement ?? null;
    reportingEventId.value = eventToReport.id;
    reportingEventTitle.value = getEventTitle(eventToReport);
    // Close the detail modal
    detailEvent.value = null;
  }
};

/**
 * Handle "Follow a Calendar" button click
 */
const emit = defineEmits(['followCalendar']);
const handleFollowCalendar = (event: MouseEvent) => {
  emit('followCalendar', event.currentTarget);
};

/**
 * Load more events when scroll sentinel becomes visible
 */
const loadMore = () => {
  if (!isLoading.value && hasMore.value) {
    loadMoreFeed();
  }
};

/**
 * Setup Intersection Observer for infinite scroll
 */
onMounted(() => {
  if (!sentinelRef.value) {
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      const sentinel = entries[0];
      if (sentinel.isIntersecting) {
        loadMore();
      }
    },
    {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    },
  );

  observer.observe(sentinelRef.value);
});

/**
 * Cleanup Intersection Observer
 */
onUnmounted(() => {
  if (observer && sentinelRef.value) {
    observer.unobserve(sentinelRef.value);
    observer.disconnect();
  }
});
</script>

<template>
  <div class="events-container">
    <div v-if="events.length" class="events-list">
      <div
        v-for="event in events"
        :key="event.id"
        class="event-item"
        data-testid="event-item"
      >
        <div class="event-content">
          <h3 class="event-title">{{ getEventTitle(event) }}</h3>
          <p class="event-date">{{ formatEventDate(event) }}</p>
          <p v-if="getEventDescription(event)" class="event-description">
            {{ getEventDescription(event) }}
          </p>
          <p class="event-source">
            {{ getCalendarIdentifier(event) }}
          </p>
          <p v-if="getEventLocation(event)" class="event-location">
            {{ getEventLocation(event) }}
          </p>
        </div>

        <div class="event-actions">
          <!-- Not reposted - show Repost button -->
          <button
            v-if="event.repostStatus === 'none'"
            type="button"
            class="repost-button"
            data-testid="repost-button"
            :aria-label="t('repost_aria_label', { eventTitle: getEventTitle(event) })"
            @click="handleRepost(event.id, $event)"
          >
            {{ t('repost_button') }}
          </button>

          <!-- Manually reposted - show clickable label to unrepost -->
          <button
            v-else-if="event.repostStatus === 'manual'"
            type="button"
            class="reposted-label"
            data-testid="reposted-label"
            :aria-label="t('unrepost_aria_label', { eventTitle: getEventTitle(event) })"
            @click="handleUnrepost(event.id)"
          >
            {{ t('reposted_button') }}
          </button>

          <!-- Auto-posted - show clickable label to unrepost -->
          <button
            v-else-if="event.repostStatus === 'auto'"
            type="button"
            class="auto-posted-label"
            data-testid="auto-posted-label"
            :aria-label="t('unrepost_auto_aria_label', { eventTitle: getEventTitle(event) })"
            @click="handleUnrepost(event.id)"
          >
            {{ t('auto_posted_label') }}
          </button>

          <button
            type="button"
            class="report-button"
            data-testid="report-button"
            :aria-label="t('report_aria_label', { eventTitle: getEventTitle(event) })"
            @click="handleReport(event, $event)"
          >
            {{ t('report_button') }}
          </button>

          <button
            type="button"
            class="details-button"
            data-testid="details-button"
            :aria-label="t('details_aria_label', { eventTitle: getEventTitle(event) })"
            @click="handleOpenDetail(event, $event)"
          >
            {{ t('details_button') }}
          </button>
        </div>
      </div>

      <!-- Scroll sentinel for infinite scroll -->
      <div
        ref="sentinelRef"
        class="scroll-sentinel"
      />

      <!-- Loading indicator -->
      <div
        v-if="isLoading"
        class="loading-indicator"
        aria-live="polite"
        aria-atomic="true"
      >
        <p>{{ t('loading_more') }}</p>
      </div>
    </div>

    <!-- Empty state: user follows calendars but no events yet -->
    <EmptyState
      v-else-if="hasFollows"
      :heading="t('no_events')"
      region
    >
      <p class="empty-waiting-message">{{ t('no_events_yet') }}</p>
    </EmptyState>

    <!-- Empty state: user follows no calendars -->
    <EmptyState
      v-else
      :heading="t('no_events')"
      region
    >
      <button
        type="button"
        class="btn btn--cta btn--lg empty-follow-button"
        @click="handleFollowCalendar($event)"
      >
        {{ t("follow_button") }}
      </button>
      <div class="empty-doc-link">
        <DocLink :guide="{ slug: 'guides/calendar-owners/follow-and-repost', key: 'follow_repost' }">
          {{ t('guide_link') }}
        </DocLink>
      </div>
    </EmptyState>

    <!-- Repost categories modal — shown when pendingRepost is set -->
    <RepostCategoriesModal
      v-if="pendingRepost"
      :event="pendingRepostEvent"
      :pre-selected-categories="pendingRepostPreSelected"
      :all-local-categories="pendingRepost.allLocalCategories"
      :source-categories="pendingRepost.sourceCategories"
      @confirm="handleRepostConfirm"
      @cancel="handleRepostCancel"
    />

    <!-- Report event modal — shown when reportingEventId is set -->
    <ReportEventModal
      v-if="reportingEventId"
      :event-id="reportingEventId"
      :event-title="reportingEventTitle"
      @close="handleReportClose"
    />

    <!-- Event detail modal — shown when detailEvent is set -->
    <FeedEventDetailModal
      v-if="detailEvent"
      :event="detailEvent"
      @close="handleCloseDetail"
      @repost="(domEvent) => handleRepost(detailEvent.id, domEvent)"
      @unrepost="handleUnrepost(detailEvent.id)"
      @report="handleReportFromModal"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@/client/assets/style/tokens/breakpoint-mixins' as *;
div.events-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;

  div.events-list {
    flex: 1;
    padding: var(--pav-space-4);

    div.event-item {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: var(--pav-space-4);
      padding: var(--pav-space-4);
      margin-bottom: var(--pav-space-3);
      background: var(--pav-color-surface-secondary);
      border: 1px solid var(--pav-color-border-primary);
      border-radius: var(--pav-border-radius-md);
      transition: box-shadow 0.2s ease;

      &:hover {
        box-shadow: var(--pav-shadow-sm);
      }

      div.event-content {
        flex: 1;
        min-width: 0; // Enable text truncation

        h3.event-title {
          margin: 0 0 var(--pav-space-1) 0;
          font-size: var(--pav-font-size-base);
          font-weight: var(--pav-font-weight-medium);
          color: var(--pav-color-text-primary);
        }

        p.event-date {
          margin: 0 0 var(--pav-space-2) 0;
          font-size: var(--pav-font-size-xs);
          color: var(--pav-color-text-secondary);
        }

        p.event-description {
          margin: 0 0 var(--pav-space-2) 0;
          font-size: var(--pav-font-size-xs);
          line-height: 1.5;
          color: var(--pav-color-text-primary);
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        p.event-source {
          margin: 0 0 var(--pav-space-1) 0;
          font-size: var(--pav-font-size-caption);
          color: var(--pav-color-text-secondary);
          font-style: italic;
        }

        p.event-location {
          margin: 0;
          font-size: var(--pav-font-size-caption);
          color: var(--pav-color-text-secondary);
        }
      }

      div.event-actions {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: var(--pav-space-2);

        button.repost-button {
          padding: var(--pav-space-2) var(--pav-space-4);
          background: var(--pav-color-orange-500);
          color: var(--pav-color-text-inverse);
          border: none;
          border-radius: var(--pav-border-radius-sm);
          font-size: var(--pav-font-size-xs);
          font-weight: var(--pav-font-weight-medium);
          cursor: pointer;
          transition: background 0.2s ease;

          &:hover {
            background: var(--pav-color-orange-600);
          }

          &:active {
            background: var(--pav-color-orange-700);
          }
        }

        button.reposted-label {
          padding: var(--pav-space-2) var(--pav-space-4);
          background: var(--pav-color-success);
          color: var(--pav-color-text-inverse);
          border: none;
          border-radius: var(--pav-border-radius-sm);
          font-size: var(--pav-font-size-xs);
          font-weight: var(--pav-font-weight-medium);
          cursor: pointer;
          transition: background 0.2s ease;

          &:hover {
            filter: brightness(0.85);
          }

          &:active {
            filter: brightness(0.75);
          }
        }

        button.auto-posted-label {
          padding: var(--pav-space-2) var(--pav-space-4);
          background: var(--pav-color-stone-500);
          color: var(--pav-color-text-inverse);
          border: none;
          border-radius: var(--pav-border-radius-sm);
          font-size: var(--pav-font-size-xs);
          font-weight: var(--pav-font-weight-medium);
          cursor: pointer;
          transition: background 0.2s ease, box-shadow 0.2s ease;

          &:hover {
            background: var(--pav-color-stone-600);
          }

          &:focus-visible {
            outline: 2px solid var(--pav-color-focus-ring, var(--pav-color-orange-500));
            outline-offset: 2px;
          }

          &:active {
            background: var(--pav-color-stone-700);
          }
        }

        button.report-button {
          padding: var(--pav-space-2) var(--pav-space-3);
          background: transparent;
          color: var(--pav-color-text-secondary);
          border: 1px solid var(--pav-color-border-primary);
          border-radius: var(--pav-border-radius-sm);
          font-size: var(--pav-font-size-xs);
          cursor: pointer;
          transition: color 0.2s ease, border-color 0.2s ease;

          &:hover {
            color: var(--pav-color-error);
            border-color: var(--pav-color-error);
          }

          &:active {
            opacity: 0.8;
          }
        }

        button.details-button {
          padding: var(--pav-space-2) var(--pav-space-3);
          background: transparent;
          color: var(--pav-color-text-secondary);
          border: 1px solid var(--pav-color-border-primary);
          border-radius: var(--pav-border-radius-sm);
          font-size: var(--pav-font-size-xs);
          cursor: pointer;
          transition: color 0.2s ease, border-color 0.2s ease;

          &:hover {
            color: var(--pav-color-text-primary);
            border-color: var(--pav-color-text-secondary);
          }

          &:active {
            opacity: 0.8;
          }
        }
      }

      @include pav-media-down(md) {
        flex-direction: column;
        gap: var(--pav-space-3);

        div.event-actions {
          width: 100%;

          button {
            width: 100%;
            text-align: center;
          }
        }
      }
    }

    div.scroll-sentinel {
      height: 1px;
      width: 100%;
    }

    div.loading-indicator {
      padding: var(--pav-space-6);
      text-align: center;

      p {
        color: var(--pav-color-text-secondary);
        font-size: var(--pav-font-size-xs);
      }
    }
  }


  p.empty-waiting-message {
    margin: 0;
    font-size: var(--pav-font-size-sm);
    color: var(--pav-color-text-secondary);
    text-align: center;
  }

  button.empty-follow-button {
    margin-block-start: var(--pav-space-3);
  }

  div.empty-doc-link {
    margin-block-start: var(--pav-space-4);
  }
}
</style>
